import React, { useState } from "react";
import XanoAPI from "../../lib/XanoAPI";
import { ClientType } from "../../types";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useToast } from "../ui/use-toast";
import { Sparkles, Loader2, AlertCircle, CheckCircle2, ArrowRight, X, Minimize2, Maximize2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";

const api = XanoAPI.getInstance();

interface SmartPasteStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPersonalDataParsed: (parsedData: Partial<ClientType>) => void;
  onFinancialDataParsed: (parsedData: Partial<ClientType>) => void;
  onPopulateSingleField: (fieldName: string, value: any) => void; // Populate one field at a time
  onSave: () => Promise<void>; // Function to save the form
  isSaveDisabled: boolean; // Whether save button should be disabled
  currentClient?: ClientType | null; // Current client data for comparison
  handleTabChange?: (tabName: string) => void; // Function to switch tabs
}

type Step = 'input' | 'review-personal' | 'review-financial' | 'complete';

export function SmartPasteStepModal({
  isOpen,
  onClose,
  onPersonalDataParsed,
  onFinancialDataParsed,
  onPopulateSingleField,
  onSave,
  isSaveDisabled,
  currentClient,
  handleTabChange,
}: SmartPasteStepModalProps) {
  const { toast } = useToast();
  const [emailContent, setEmailContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('input');
  const [personalFields, setPersonalFields] = useState<Partial<ClientType>>({});
  const [financialFields, setFinancialFields] = useState<Partial<ClientType>>({});
  const [personalFieldsPopulated, setPersonalFieldsPopulated] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());

  // Define which fields are personal vs financial
  const personalFieldKeys = [
    'firstName', 'lastName', 'gender', 'email', 'phoneNumber', 'mobilePhone',
    'dateOfBirth', 'address', 'city', 'stateProvince', 'zipCode',
    'spousesName', 'spousesDateOfBirth', 'dependents', 'related_client_id',
    'targetRetirementAge', 'targetRetirementMonth', 'lifeExpectancyAge',
    'interestRatePriorToRetirement', 'interestRateInRetirement',
    'annuallyIncreaseSavingsWithRaises', 'federalAgency', 'lastContactDate',
    'preferredContactMethod', 'contactSource', 'type', 'status'
  ];

  const handleSubmit = async () => {
    if (!emailContent.trim()) {
      setError("Please paste some content to parse.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setPersonalFieldsPopulated(false); // Reset populated state for new parse

    try {
      const parsedData = await api.smartPasteClient(emailContent);
      
      console.log('🤖 RAW AI RESPONSE:');
      console.log('   Full parsed data:', parsedData);
      console.log('   All keys from AI:', Object.keys(parsedData));
      console.log('   firstName in response?', 'firstName' in parsedData);
      console.log('   lastName in response?', 'lastName' in parsedData);
      console.log('   AI firstName value:', parsedData.firstName);
      console.log('   AI lastName value:', parsedData.lastName);

      // Separate personal and financial fields
      const personal: Partial<ClientType> = {};
      const financial: Partial<ClientType> = {};

      Object.entries(parsedData).forEach(([key, value]) => {
        if (personalFieldKeys.includes(key)) {
          personal[key as keyof ClientType] = value;
        } else {
          financial[key as keyof ClientType] = value;
        }
      });

      setPersonalFields(personal);
      setFinancialFields(financial);

      console.log('📊 Parsed data separated:');
      console.log('  Personal fields:', Object.keys(personal).length);
      console.log('  Personal field keys:', Object.keys(personal));
      console.log('  Financial fields:', Object.keys(financial).length);
      console.log('  Personal object:', personal);

      if (Object.keys(personal).length > 0) {
        setCurrentStep('review-personal');
      } else if (Object.keys(financial).length > 0) {
        setCurrentStep('review-financial');
      } else {
        setError("No recognizable client information found in the content.");
      }

      toast({
        title: "✅ Content Parsed Successfully",
        description: `Found ${Object.keys(personal).length} personal and ${Object.keys(financial).length} financial fields.`,
      });
    } catch (err) {
      console.error("Smart Paste Error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to parse content";
      setError(errorMessage);
      toast({
        title: "❌ Parsing Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    console.log("💾 [Modal] Save button clicked");
    console.log("   isSaveDisabled:", isSaveDisabled);
    console.log("   onSave function:", typeof onSave);
    
    if (isSaveDisabled) {
      console.warn("⚠️ [Modal] Save button is disabled, cannot save");
      toast({
        title: "⚠️ Cannot Save",
        description: "Form is not valid or has no changes. Please check the form and try again.",
        variant: "destructive",
      });
      return;
    }
    
    if (!onSave || typeof onSave !== 'function') {
      console.error("❌ [Modal] onSave handler is not a function:", onSave);
      toast({
        title: "❌ Save Failed",
        description: "Save handler is not available. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSaving(true);
    try {
      console.log("💾 [Modal] Calling onSave()...");
      await onSave();
      console.log("✅ [Modal] onSave() completed successfully");
      toast({
        title: "✅ Saved Successfully",
        description: "Your changes have been saved. The modal will remain open so you can continue working.",
      });
      // DO NOT close modal after save - keep it open for continued work
      // The user can manually close it when done
    } catch (err) {
      console.error("❌ [Modal] Save error:", err);
      toast({
        title: "❌ Save Failed",
        description: err instanceof Error ? err.message : "Failed to save. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmPersonal = () => {
    console.log('🔵 ==============================================');
    console.log('🔵 POPULATE BUTTON CLICKED');
    console.log('🔵 ==============================================');
    console.log('📋 Personal fields to populate:', personalFields);
    console.log('📊 Number of fields:', Object.keys(personalFields).length);
    console.log('🔍 CRITICAL CHECK - firstName and lastName:');
    console.log('   personalFields.firstName =', personalFields.firstName);
    console.log('   personalFields.lastName =', personalFields.lastName);
    console.log('   Are they in the object?', 'firstName' in personalFields, 'lastName' in personalFields);
    console.log('🔍 Function type check:');
    console.log('   typeof onPersonalDataParsed =', typeof onPersonalDataParsed);
    console.log('   onPersonalDataParsed =', onPersonalDataParsed);
    
    if (Object.keys(personalFields).length === 0) {
      console.error('❌ No personal fields to populate!');
      toast({
        title: "⚠️ No Fields to Populate",
        description: "No personal fields were found to populate.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('✅ ABOUT TO CALL onPersonalDataParsed()...');
    console.log('   With data:', JSON.stringify(personalFields, null, 2));
    
    try {
      onPersonalDataParsed(personalFields);
      console.log('✅ onPersonalDataParsed() CALLED SUCCESSFULLY');
    } catch (error) {
      console.error('❌ ERROR calling onPersonalDataParsed:', error);
    }
    
    setPersonalFieldsPopulated(true);
    
    toast({
      title: "✨ Fields Populated",
      description: `${Object.keys(personalFields).length} personal fields have been filled. Scroll up to review the changes.`,
    });
  };

  const handleContinueToFinancial = () => {
    if (Object.keys(financialFields).length > 0) {
      // Switch to financial tab if handler is available
      if (handleTabChange) {
        handleTabChange('financial-information');
        toast({
          title: "📊 Switched to Financial Tab",
          description: "Review and apply financial fields below",
          duration: 3000,
        });
      }
      // Has financial data - move to review
      setCurrentStep('review-financial');
    } else {
      // No financial data - complete
      handleClose();
    }
  };

  const handleConfirmFinancial = () => {
    console.log('✅ User confirmed financial information');
    onFinancialDataParsed(financialFields);
    setCurrentStep('complete');
    
    setTimeout(() => {
      handleClose();
    }, 2000);
  };

  const handleClose = () => {
    setEmailContent("");
    setError(null);
    setCurrentStep('input');
    setPersonalFields({});
    setFinancialFields({});
    setPersonalFieldsPopulated(false);
    onClose();
  };

  // Format field name for display
  const formatFieldName = (field: string): string => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Apply a single field
  const handleApplySingleField = (fieldName: string, value: any) => {
    onPopulateSingleField(fieldName, value);
    setAppliedFields(prev => new Set(prev).add(fieldName));
    toast({
      title: "Field Applied",
      description: `${formatFieldName(fieldName)} has been updated`,
      duration: 2000,
    });
  };

  // Apply all fields at once
  const handleApplyAllFields = (fields: Partial<ClientType>, isPersonal: boolean) => {
    if (isPersonal) {
      onPersonalDataParsed(fields);
      setPersonalFieldsPopulated(true);
      // Mark all as applied
      setAppliedFields(new Set(Object.keys(fields)));
    } else {
      onFinancialDataParsed(fields);
      // Mark all as applied
      setAppliedFields(prev => {
        const newSet = new Set(prev);
        Object.keys(fields).forEach(key => newSet.add(key));
        return newSet;
      });
    }
  };

  const renderFieldsList = (fields: Partial<ClientType>) => {
    const fieldNames = Object.keys(fields);
    if (fieldNames.length === 0) return null;

    return (
      <div className="bg-gray-50 rounded-md p-3 max-h-[150px] overflow-y-auto">
        <div className="grid grid-cols-3 gap-2 text-sm">
          {fieldNames.map(field => (
            <div key={field} className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-accent flex-shrink-0" />
              <span className="text-gray-700 truncate">{field}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render field-by-field list with apply buttons (enhanced UI)
  const renderFieldByFieldList = (fields: Partial<ClientType>) => {
    const fieldEntries = Object.entries(fields);
    if (fieldEntries.length === 0) return null;

    const displayValue = (val: any) => {
      if (val === null || val === undefined || val === '') {
        return <span className="text-gray-400 italic text-xs">empty</span>;
      }
      if (typeof val === 'boolean') {
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {val ? 'Yes' : 'No'}
          </span>
        );
      }
      const strVal = String(val);
      return strVal.length > 40 ? strVal.substring(0, 40) + '...' : strVal;
    };

                const hasChanges = (fieldName: string, newValue: any) => {
      const currentValue = currentClient?.[fieldName as keyof ClientType];
      if (currentValue === newValue) return false;
      if (!currentValue && !newValue) return false;
      return true;
    };

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="max-h-[450px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10 border-b-2 border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[30%]">
                  Field Name
                </th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[28%]">
                  <div className="flex items-center gap-1">
                    Current Value
                    <span className="text-xs font-normal text-gray-500">(from DB)</span>
                  </div>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[28%]">
                  <div className="flex items-center gap-1">
                    New Value
                    <span className="text-xs font-normal text-gray-500">(from AI)</span>
                  </div>
                </th>
                <th className="text-center px-4 py-3 font-semibold text-gray-700 w-[14%]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {fieldEntries.map(([fieldName, newValue]) => {
                const isApplied = appliedFields.has(fieldName);
                const currentValue = currentClient?.[fieldName as keyof ClientType];
                const isChanged = hasChanges(fieldName, newValue);

                return (
                  <tr 
                    key={fieldName} 
                    className={`transition-colors ${
                      isApplied 
                        ? 'bg-green-50 border-l-4 border-l-green-500' 
                        : isChanged
                          ? 'hover:bg-accent/5'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isApplied && (
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                        <span className={`font-medium ${isApplied ? 'text-green-900' : 'text-gray-900'}`}>
                          {formatFieldName(fieldName)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-600 max-w-[250px] truncate" title={String(currentValue || '')}>
                        {displayValue(currentValue)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`font-medium max-w-[250px] truncate ${
                        isChanged ? 'text-accent' : 'text-gray-500'
                      }`} title={String(newValue || '')}>
                        {isChanged && !isApplied && (
                          <span className="inline-block w-2 h-2 bg-accent rounded-full mr-2 animate-pulse"></span>
                        )}
                        {displayValue(newValue)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isApplied ? (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 border border-green-300 rounded-md text-green-700 text-xs font-semibold">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Applied
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 text-xs font-medium border-accent text-accent hover:bg-accent hover:text-white transition-all"
                          onClick={() => handleApplySingleField(fieldName, newValue)}
                        >
                          <ArrowRight className="h-3 w-3 mr-1" />
                          Apply
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Summary footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-2.5 flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 bg-accent rounded-full animate-pulse"></span>
              <strong>{fieldEntries.filter(([k, v]) => hasChanges(k, v) && !appliedFields.has(k)).length}</strong> pending
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-green-600" />
              <strong>{appliedFields.size}</strong> applied
            </span>
          </div>
          <span className="text-gray-500">
            {fieldEntries.length} total field{fieldEntries.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed left-0 right-0 transition-all duration-300 ${
        isMinimized ? 'bottom-0 z-40' : 'bottom-0 z-50'
      }`}
    >
      <Card className={`mx-auto max-w-4xl shadow-2xl border-2 border-accent rounded-t-lg rounded-b-none ${
        isMinimized ? 'shadow-md' : 'shadow-2xl'
      }`}>
        <CardHeader 
          className={`flex-shrink-0 border-b bg-white ${
            isMinimized 
              ? 'pb-2 pt-2 cursor-pointer hover:bg-gray-50' 
              : 'pb-3'
          }`}
          onClick={isMinimized ? () => setIsMinimized(false) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Sparkles className="h-5 w-5 text-accent" />
              <CardTitle className={isMinimized ? "text-base" : "text-lg"}>
                Smart Paste
                {!isMinimized && ' - AI Content Parser'}
              </CardTitle>
              {!isMinimized && currentStep !== 'input' && (
                <span className="text-xs font-normal text-gray-500">
                  {currentStep === 'review-personal' && '(Step 1: Personal Info)'}
                  {currentStep === 'review-financial' && '(Step 2: Financial Info)'}
                  {currentStep === 'complete' && '(Complete)'}
                </span>
              )}
              {isMinimized && (
                <div className="flex items-center gap-2 ml-2">
                  <span className="text-xs text-gray-500">-</span>
                  {currentStep === 'review-personal' && !personalFieldsPopulated && (
                    <span className="px-2 py-0.5 bg-accent/10 border border-accent rounded text-xs text-accent">
                      Ready to populate
                    </span>
                  )}
                  {currentStep === 'review-personal' && personalFieldsPopulated && (
                    <span className="px-2 py-0.5 bg-green-100 border border-green-600 rounded text-xs text-green-700">
                      Fields populated
                    </span>
                  )}
                  {currentStep === 'review-financial' && (
                    <span className="px-2 py-0.5 bg-accent/10 border border-accent rounded text-xs text-accent">
                      Financial ready
                    </span>
                  )}
                  {currentStep === 'input' && (
                    <span className="px-2 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs text-gray-600">
                      Awaiting input
                    </span>
                  )}
                  <span className="text-xs text-accent">(click to expand)</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(!isMinimized);
                }}
                title={isMinimized ? "Expand panel" : "Minimize panel"}
              >
                {isMinimized ? (
                  <Maximize2 className="h-4 w-4" />
                ) : (
                  <Minimize2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                disabled={isLoading || isSaving}
                title="Close Smart Paste"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {!isMinimized && (
            <CardDescription className="text-xs mt-2">
              {currentStep === 'input' && 
                "Paste email or text containing client information. Our AI will automatically extract and organize the data."}
              {currentStep === 'review-personal' && 
                "Review personal information fields that will be populated. Scroll up to see form updates in real-time. You can minimize this panel and switch tabs to review all changes."}
              {currentStep === 'review-financial' && 
                "After saving personal information, these financial fields will be populated. Switch to the Financial Information tab to review."}
              {currentStep === 'complete' && 
                "All data has been populated successfully! Review and save your changes."}
            </CardDescription>
          )}
        </CardHeader>

        {!isMinimized && (
          <>
            <CardContent className="max-h-[60vh] overflow-y-auto space-y-4 py-4 bg-white">
              {currentStep === 'input' && (
                <>
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Paste your email or text content here..."
                      value={emailContent}
                      onChange={(e) => {
                        setEmailContent(e.target.value);
                        setError(null);
                      }}
                      className="min-h-[200px] font-mono text-sm"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500">
                      The AI will separate personal information (name, address, contact) from financial information (salary, TSP, retirement plans).
                    </p>
                  </div>
                </>
              )}

              {currentStep === 'review-personal' && (
                <>
                  <Alert className="bg-green-50 border-2 border-accent">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <AlertTitle className="text-accent">Personal Information Parsed</AlertTitle>
                    <AlertDescription className="text-gray-700">
                      Found {Object.keys(personalFields).length} personal fields. Apply them individually or all at once.
                    </AlertDescription>
                  </Alert>
                  
                  {renderFieldByFieldList(personalFields)}
                  
                  <Alert className="bg-accent/10 border-2 border-accent">
                    <AlertCircle className="h-4 w-4 text-accent" />
                    <AlertDescription className="text-gray-700">
                      <strong>Tip:</strong> Click <strong>Apply</strong> next to each field to populate it individually, or use <strong>Apply All Fields</strong> below.
                      {Object.keys(financialFields).length > 0 && " Financial fields will be available after saving."}
                    </AlertDescription>
                  </Alert>
                </>
              )}

              {currentStep === 'review-financial' && (
                <>
                  <Alert className="bg-green-50 border-2 border-accent">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <AlertTitle className="text-accent">Financial Information Parsed</AlertTitle>
                    <AlertDescription className="text-gray-700">
                      Found {Object.keys(financialFields).length} financial fields. Apply them individually or all at once.
                    </AlertDescription>
                  </Alert>
                  
                  {renderFieldByFieldList(financialFields)}
                  
                  <Alert className="bg-accent/10 border-2 border-accent">
                    <AlertCircle className="h-4 w-4 text-accent" />
                    <AlertDescription className="text-gray-700">
                      <strong>Tip:</strong> Click <strong>Apply</strong> next to each field to populate it individually, or use <strong>Apply All Fields</strong> below.
                    </AlertDescription>
                  </Alert>
                </>
              )}

              {currentStep === 'complete' && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-900">All Done!</AlertTitle>
                  <AlertDescription className="text-green-800">
                    All fields have been populated. Please review and save your changes.
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>

            <CardFooter className="flex-shrink-0 flex flex-wrap gap-2 justify-end border-t pt-4 bg-white">
              {currentStep === 'input' && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !emailContent.trim()}
              className="bg-accent"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Parsing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Parse Content
                </>
              )}
            </Button>
          )}

          {currentStep === 'review-personal' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleApplyAllFields(personalFields, true)}
                disabled={appliedFields.size === Object.keys(personalFields).length}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {appliedFields.size === Object.keys(personalFields).length 
                  ? 'All Fields Applied' 
                  : `Apply All (${Object.keys(personalFields).length})`
                }
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaveDisabled || isSaving}
                className="bg-green-600"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
              {Object.keys(financialFields).length > 0 && (
                <Button
                  type="button"
                  onClick={handleContinueToFinancial}
                  variant="outline"
                  disabled={isSaving}
                >
                  Continue to Financial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </>
          )}

          {currentStep === 'review-financial' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleApplyAllFields(financialFields, false)}
                disabled={appliedFields.size >= Object.keys(personalFields).length + Object.keys(financialFields).length}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {appliedFields.size >= Object.keys(personalFields).length + Object.keys(financialFields).length
                  ? 'All Fields Applied' 
                  : `Apply All (${Object.keys(financialFields).length})`
                }
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaveDisabled || isSaving}
                className="bg-green-600"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          )}

              {currentStep === 'complete' && (
                <Button
                  type="button"
                  onClick={handleClose}
                  className="bg-accent"
                >
                  Done
                </Button>
              )}
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}

